import { FileSearch, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import PreApprovalChangeHistoryTab from "../audit/PreApprovalChangeHistoryTab";
import StatusHistoryTab from "../audit/StatusHistoryTab";
import ViewAllNotesTab from "../audit/ViewAllNotesTab";
import FabButton from "./FabButton";
import { AUDIT_TABS } from "./mockData";

export const INTERNAL_AUDIT_PANEL_ID = "internal-audit-panel";

type InternalAuditDrawerProperties = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The deal / approval whose audit data the drawer shows. */
  approvalId: string;
};

function InternalAuditDrawer({ open, onOpenChange, approvalId }: InternalAuditDrawerProperties) {
  const { t } = useTranslation();
  const firstTabId = AUDIT_TABS[0].id;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/*
       * SheetTrigger is not used here because Radix Dialog/Sheet's trigger
       * and FAB are separate concerns in the cluster. The FAB button controls
       * open state through the cluster's onOpenChange prop instead.
       */}
      <FabButton
        label={t("actions.internalAudit")}
        icon={<FileSearch className="size-6" />}
        variant="purple"
        expanded={open}
        controls={INTERNAL_AUDIT_PANEL_ID}
        hasPopup="dialog"
        onClick={() => onOpenChange(!open)}
      />
      <SheetContent
        id={INTERNAL_AUDIT_PANEL_ID}
        side="right"
        className="w-full p-0 sm:max-w-xl md:max-w-2xl lg:max-w-3xl"
      >
        <SheetHeader className="border-border flex-row items-center justify-between border-b px-4 py-3">
          <SheetTitle>{t("actions.internalAudit")}</SheetTitle>
          <SheetDescription className="sr-only">{t("actions.internalAuditDescription")}</SheetDescription>
          <SheetClose
            className="text-muted-foreground hover:text-foreground rounded-sm transition-colors"
            aria-label={t("actions.closeInternalAudit")}
          >
            <X className="size-5" aria-hidden="true" />
          </SheetClose>
        </SheetHeader>

        <div className="flex flex-1 flex-col overflow-hidden px-4 py-4">
          <Tabs defaultValue={firstTabId} className="flex flex-1 flex-col overflow-hidden">
            <TabsList className="h-auto w-full flex-wrap justify-start gap-1 bg-transparent p-0">
              {AUDIT_TABS.map((tab) => (
                <TabsTrigger
                  key={tab.id}
                  value={tab.id}
                  className="data-[state=active]:border-primary data-[state=active]:text-primary rounded-none border-b-2 border-transparent px-3 py-2 text-sm data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {AUDIT_TABS.map((tab) => (
              <TabsContent key={tab.id} value={tab.id} className="mt-4 min-h-0 flex-1 overflow-y-auto">
                {tab.id === "view-all-notes" ? (
                  <ViewAllNotesTab approvalId={approvalId} />
                ) : tab.id === "pre-approval-history" ? (
                  <PreApprovalChangeHistoryTab approvalId={approvalId} />
                ) : tab.id === "status-history" ? (
                  <StatusHistoryTab approvalId={approvalId} />
                ) : (
                  <div className="text-muted-foreground flex h-32 items-center justify-center rounded-lg border border-dashed text-sm">
                    {tab.label} — placeholder
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default InternalAuditDrawer;
